import { Component } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, FormControl, ValidatorFn } from '@angular/forms';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css']
})
export class AppComponent {
  title = 'checkliste';
  currentStep: number = 1;
  isCompleted: boolean = false;
  formdata: any = FormGroup;
  appHasPassed: boolean = true;
  rating: number = 0.0;
  conclusion: string = '';

  wizard1 = {
    choices: [
      { 
        weight: 5, 
        name: '<p>The specific scope of the app is clearly described, e.g. daily reminders to take medication.</p>',
        text: { title: 'Purpose and functionality', description: 'Regardless of your individual requirements for the app, in order for it to run safely and reliably the following criteria should be met:'}
      },
      { 
        weight: 5, 
        name: 'The app lists its own limitations (e.g. disclaimer stating that the app cannot replace a real-life medical consultation).'
      },
      { 
        weight: 2.5, 
        name: 'The last update was provided less than six months ago (as a rule of thumb).',
      },
      { 
        weight: 2.5, 
        name: 'The app runs reliably and does not crash or restart unprompted.',
      },
      {  
        weight: 10, 
        name: 'The app does <u>not</u> provide a definitive diagnosis and corresponding treatment recommendations.',
        text: {title: 'Quality and Evaluation', description: 'Check whether the following two criteria definitely apply to a given app:'}
      },
      {  
        weight: 5, 
        name: 'The app does support existing treatment plans (e.g. by recording biometrics).' 
      },
      {  
        weight: 2.5, 
        name: 'The app has frequently been rated positively by a many different users.',
        text: { title: 'Ratings by other users', description: ''}
      }
    ]
  };

  wizard2 = {
    choices: [
      { 
        weight: 5, 
        name: 'The app has been certified and awarded a trustworthy certification mark.',
        text: { title: 'Quality certificates and certification marks', description: ''}
      },
      {  
        weight: 2.5, 
        name: 'The evaluation criteria associated with the certification mark are clear and understandable.'
      },
      { 
        weight: 5,
        ko_criteria: 1, 
        is_parent: 1,
        name: 'The app provides a data privacy notice, which…',
        text: { title: 'Data privacy notice', description: 'Generally, an app should provide a data privacy notice covering the following points:'}
      },
      { 
        weight: 5, 
        is_sub: 1,
        name: 'is easy to find and to view (e.g. directly within the application, on its websites or in app stores),',
      },
      { 
        weight: 5, 
        is_sub: 1,
        name: 'provides information about the type, extent and purpose of data collection as well as the particulars of how data are processed and whether they are passed on to third parties,' 
      },
      { 
        weight: 5, 
        is_sub: 1,
        name: 'specifies where (in which country) and how (encrypted/anonymized) your data are stored,',
      },
      { 
        weight: 5, 
        is_sub: 1,
        name: 'specifies whether and how (via email or post etc.) you can withdraw your consent for data storage.' 
      },
      { 
        weight: 5, 
        name: 'The app only requests data that are important to its functionality.',
        text: { title: 'Access to functionality and data', description: 'Generally, apps that request and/or store your personal data should have the following characteristics:'}
      },
      { 
        weight: 5, 
        name: 'Requests for access to functions of the mobile device (e.g. access to location via GPS or to the calendar) are only requested in order to facilitate app usage.' 
      },
      { 
        weight: 5, 
        name: 'The app specifies that it is possible for collected and stored data to be deleted. Given that it is rarely possible to delete data irretrievably, you should be careful in deciding which data to share.' 
      }
    ]
  };

  wizard3 = {
    choices: [
      { 
        weight: 5, 
        ko_criteria: 1,
        name: 'The name and address of the provider. Legal entities (e.g. Inc., PLC, Ltd., Co.) in particular must state their legal form and authorized representatives.',
        text: { title: 'Imprint', description: 'The imprint of an app is required to contain at least:'},
      },
      { 
        weight: 5, 
        name: 'Details for direct and immediate contact (telephone or fax number, email address).',
      },
      { 
        weight: 5, 
        name: 'The financial background of an app is apparent if, for example,  <br><br> • users pay for the app and it finances itself, <br><br> • the costs for the app are reimbursed by health insurance providers (this is very rare),  <br><br> • sponsors and/or public funding finance the app.<br><br>',
        text: { title: 'Funding and financial background', description: ''}, 
      },
      { 
        weight: 5, 
        name: 'The app is neutral, meaning that its contents are not influenced by commercial interests, if for example, <br><br> • the app does not advertise a specific product, <br><br> • the app is provided by a public or charitable organization.',
      }
    ]
  };


  constructor(private formsModule: FormsModule, private formBuilder: FormBuilder) {
    this.formdata = this.formBuilder.group({
      wizard1_choices: new FormArray([]),
      wizard2_choices: new FormArray([]),
      wizard3_choices: new FormArray([])
    });

    this.addCheckboxes();
  }

  private addCheckboxes() {
    this.wizard1.choices.map((o, i) => {
      const control = new FormControl(false); // checked = false
      (this.formdata.controls.wizard1_choices as FormArray).push(control);
    });

    this.wizard2.choices.map((o, i) => {
      const control = new FormControl(false); // checked = false
      (this.formdata.controls.wizard2_choices as FormArray).push(control);
    });

    this.wizard3.choices.map((o, i) => {
      const control = new FormControl(false); // checked = false
      (this.formdata.controls.wizard3_choices as FormArray).push(control);
    });
  }

  onCheckboxChange(checkbox, checked) {
    this.formdata.controls.wizard2_choices.value.forEach((value, index) => {
      if(checkbox.hasOwnProperty('is_parent') && this.wizard2.choices[index].hasOwnProperty('is_sub')) {
        if(!checked) {
          this.formdata.controls.wizard2_choices.controls[index].setValue(false);
        }
      } else if(checkbox.hasOwnProperty('is_sub') && this.wizard2.choices[index].hasOwnProperty('is_parent')) {
        if(checked) {
          this.formdata.controls.wizard2_choices.controls[index].setValue({value: true});
        }
      }
    });
  }

  onStep1Next() {
    this.currentStep = 2;
    window.top.scroll(0,0);
    window.scroll(0,0);
  }

  onStep2Next() {
    this.currentStep = 3;
    window.top.scroll(0,0);
    window.scroll(0,0);
  }

  onStep3Next() {
    this.currentStep = 3;
    window.top.scroll(0,0);
    window.scroll(0,0);
  }

  onComplete() {
      window.top.scroll(0,0);
      window.scroll(0,0);
     this.isCompleted = true;

     this.formdata.controls.wizard1_choices.value.forEach((choice, index) => {
       if(this.wizard1.choices[index].hasOwnProperty('ko_criteria') && !choice) {
         this.appHasPassed = false;
       } else if(choice) {
         this.rating += this.wizard1.choices[index].weight;
       }
     });

     this.formdata.controls.wizard2_choices.value.forEach((choice, index) => {
       if(this.wizard2.choices[index].hasOwnProperty('ko_criteria') && !choice) {
         this.appHasPassed = false;
       } else if(choice) {
         this.rating += this.wizard2.choices[index].weight;
       }
     });

     this.formdata.controls.wizard3_choices.value.forEach((choice, index) => {
       if(this.wizard3.choices[index].hasOwnProperty('ko_criteria') && !choice) {
         this.appHasPassed = false;
       } else if(choice) {
         this.rating += this.wizard3.choices[index].weight;
       }
     });

     if(this.appHasPassed && this.rating >= 0 && this.rating <= 40) {
       this.conclusion = "The app you are reviewing has very low compliance with the recommended health app requirements. You should be aware of the risks when using this app anyway.";
     } else if(this.appHasPassed && this.rating > 40 && this.rating <= 84) {
       this.conclusion = "The app you reviewed matches many of the recommended criteria. Nevertheless, some criteria are not met. Please decide for yourself whether the fulfilled criteria are sufficient for you.";
     } else if(this.appHasPassed && this.rating > 84 && this.rating <= 99) {
       this.conclusion = "The app you reviewed meets almost all the criteria recommended by the German Coalition of Patient Safety / Aktionsbündnis Patientensicherheit.";
     } else if(this.appHasPassed && this.rating > 99) {
       this.conclusion = "The app you reviewed meets all the criteria recommended by the German Coalition of Patient Safety / Aktionsbündnis Patientensicherheit.";
     } else if(!this.appHasPassed) {
       this.conclusion = "There is no privacy policy and / or imprint in the tested app, so we advise against using this app.";
     }
  }

  exit() {
    window.top.location.href = 'https://www.aps-ev.de/'; //window.top.location.origin
  }

  reload() {
    window.location.reload();
    window.top.scroll(0,0);
    window.scroll(0,0);
  }
}
